import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { getProjects, getUserGroupsForProject, getUserConfig } from '../../services/platformApi';
import Header from '../../components/Header';
import Loading from '../../components/Loading';
import ProjectCard from '../../components/ProjectCard';
import type { Project, UserGroup } from '../../types/config';
import './HomePage.css';

// Helper to check if a user group belongs to a project
function userGroupBelongsToProject(userGroup: UserGroup, projectId: string): boolean {
  const projectWorkspace = userGroup._userAttributes?.project_workspace;
  if (!projectWorkspace) {
    console.log('No project_workspace attribute', userGroup);
    // If no project_workspace attribute, include it (backward compatibility)
    return false;
  }
  
  // Check if it's an array
  if (Array.isArray(projectWorkspace)) {
    return projectWorkspace.some(pw => {
      if (typeof pw === 'string') return pw === projectId;
      if (typeof pw === 'object' && pw !== null) {
        return pw._id === projectId || pw.id === projectId;
      }
      return false;
    });
  }
  
  // Check if it's an object with _id or id
  if (typeof projectWorkspace === 'object') {
    return projectWorkspace._id === projectId || projectWorkspace.id === projectId;
  }
  
  // Check if it's a string (direct ID)
  if (typeof projectWorkspace === 'string') {
    return projectWorkspace === projectId;
  }
  
  return true;
}

export function HomePage() {
  const navigate = useNavigate();
  const { createCtx } = useAuth();
  const { 
    applicationNamespaces,
    setSelectedProject, 
    setSelectedUserGroup,
    setUserConfig,
    storedState 
  } = useApp();

  const [projects, setProjects] = useState<Project[]>([]);
  const [projectUserGroups, setProjectUserGroups] = useState<Record<string, UserGroup[]>>({});
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Load projects
  useEffect(() => {
    const loadProjects = async () => {
      setLoadingProjects(true);
      setError(null);

      console.log('Loading projects with namespaces:', applicationNamespaces);

      try {
        const ctx = createCtx(applicationNamespaces);
        console.log('Context created:', { hasToken: !!ctx.authToken, namespaces: ctx._namespaces });
        
        const projectList = await getProjects(ctx);
        console.log('Projects loaded:', projectList.length, projectList.map(p => p._name));
        setProjects(projectList);

        // If there's a stored project, try to restore it
        if (storedState.projectId) {
          const storedProject = projectList.find(p => p._id === storedState.projectId);
          if (storedProject) {
            // Load user groups for this project
            await loadUserGroupsForProject(storedProject);
          }
        }
      } catch (err) {
        console.error('Failed to load projects:', err);
        setError('Failed to load projects. Please try again.');
      } finally {
        setLoadingProjects(false);
      }
    };

    loadProjects();
  }, [applicationNamespaces]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load user groups for a project
  const loadUserGroupsForProject = useCallback(async (project: Project): Promise<UserGroup[]> => {
    if (projectUserGroups[project._id]) {
      return projectUserGroups[project._id];
    }

    setLoadingGroups(prev => new Set([...prev, project._id]));

    try {
      const ctx = createCtx(applicationNamespaces);
      const allGroups = await getUserGroupsForProject(project._namespaces, ctx);

      console.info('allGroups', allGroups);
      // Filter groups that belong to this project
      const filteredGroups = allGroups.filter(group => 
        userGroupBelongsToProject(group, project._id)
      );
      
      setProjectUserGroups(prev => ({
        ...prev,
        [project._id]: filteredGroups,
      }));

      return filteredGroups;
    } catch (err) {
      console.error('Failed to load user groups:', err);
      return [];
    } finally {
      setLoadingGroups(prev => {
        const next = new Set(prev);
        next.delete(project._id);
        return next;
      });
    }
  }, [createCtx, applicationNamespaces, projectUserGroups]);

  // Handle project card hover to preload user groups
  const handleProjectHover = (project: Project) => {
    if (!projectUserGroups[project._id] && !loadingGroups.has(project._id)) {
      loadUserGroupsForProject(project);
    }
  };

  // Handle project card click to load user groups
  const handleProjectClick = async (project: Project) => {
    if (!projectUserGroups[project._id] && !loadingGroups.has(project._id)) {
      await loadUserGroupsForProject(project);
    }
  };

  // Handle project and user group selection
  const handleProjectSelect = async (project: Project, userGroup: UserGroup) => {
    setSelectedProject(project);
    setSelectedUserGroup(userGroup);

    // Load user config for this user group
    try {
      const ctx = createCtx([...userGroup._namespaces, ...project._namespaces, ...applicationNamespaces]);
      const config = await getUserConfig(userGroup, project, ctx);
      if (config) {
        setUserConfig(config);
      }
    } catch (err) {
      console.error('Failed to load user config:', err);
    }

    // Navigate to project page
    navigate(`/project/${project._id}`);
  };

  const handleLogoClick = () => {
    // Already on home, maybe refresh
    window.location.reload();
  };

  if (loadingProjects) {
    return <Loading message="Loading projects..." fullScreen />;
  }

  return (
    <div className="home-page">
      <Header onLogoClick={handleLogoClick} />
      
      <main className="home-content">
        <div className="home-header">
          <h1 className="home-title">Your Projects</h1>
          <p className="home-subtitle">
            Select a project and user group to continue
          </p>
        </div>

        {error && (
          <div className="home-error">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
            <button onClick={() => window.location.reload()}>
              <i className="fas fa-redo"></i>
              Retry
            </button>
          </div>
        )}

        {projects.length === 0 ? (
          <div className="home-empty">
            <div className="empty-icon">
              <i className="fas fa-folder-open"></i>
            </div>
            <h2>No projects found</h2>
            <p>You don't have access to any projects in this application.</p>
          </div>
        ) : (
          <div className="projects-grid">
            {projects.map(project => (
              <div
                key={project._id}
                onMouseEnter={() => handleProjectHover(project)}
                onClick={() => handleProjectClick(project)}
              >
                <ProjectCard
                  project={project}
                  userGroups={projectUserGroups[project._id] || []}
                  onUserGroupSelect={handleProjectSelect}
                  isLoading={loadingGroups.has(project._id)}
                  hasLoadedGroups={project._id in projectUserGroups}
                />
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default HomePage;
